"use client";
import React from "react";
import Timestamp from "../timestamp";
import Chip from "@/components/primitives/chip";
import Badge from "@/components/primitives/badge";
import {
  GameDetail,
  formatSeason,
  formatServerSeason,
  isGameClosed,
  isGameException,
} from "common";
import PickGroup from "@/components/pick/group";
import TeamLogo from "@/components/team/logo";
import {
  formatGameStatus,
  getFinalPeriod,
  getGameWinner,
  getTotalScores,
  isGameLive,
} from "@/utils/game";
import ViewPicks from "./view-picks";
import { useWebSocketMessage } from "@/utils/useWebSocketMessage";
import PickChip from "./pick-chip";
import Link from "next/link";
import { createTeamSlug } from "@/utils/url";
import GameDetailScore from "./score";
export default function GameDetailHeader({ game }: { game: GameDetail }) {
  const [data, setData] = React.useState<GameDetail>(game);
  const wsGames = useWebSocketMessage("scoreboard") || [];
  const wsGamePickUpdates = useWebSocketMessage("game_picks_updated") || [];
  
  React.useEffect(() => {
    if (!wsGamePickUpdates || wsGamePickUpdates.length === 0) return;
    const update: { gameId: number; totalPicks: number } =
      wsGamePickUpdates.find(
        (gamePickUpdate: { gameId: number; totalPicks: number }) =>
          Number(gamePickUpdate.gameId) === game.id
      );
    if (update) {
      const newData = {
        ...data,
        totalPicks: update.totalPicks
      };
      setData(newData);
    }
  }, [wsGamePickUpdates]);

  React.useEffect(() => {
    if (!Array.isArray(wsGames) || !data) return;

    const update = wsGames.find((wsGame) => wsGame.id === data.id);
    if (update) {
      const newData = {
        ...data,
        ...update,
        // bestOdds: {...data.bestOdds},
        odds: {
          ...data.odds,
          ...(update.odds && update.odds),
        },
        awayTeam: {
          ...game.awayTeam,
          ...(update.awayTeam && update.awayTeam),
        },
        homeTeam: {
          ...game.homeTeam,
          ...(update.homeTeam && update.homeTeam),
        },
        awayRecord: {
          ...game.awayRecord,
          ...(update.awayRecord && update.awayRecord),
        },
        homeRecord: {
          ...game.homeRecord,
          ...(update.homeRecord && update.homeRecord),
        },
        status: update.status || data.status,
        period: update.period || data.period,
      };

      setData(newData);
    }
  }, [wsGames]);

  if (data) {
    console.log(data);
    let live = isGameLive(data, undefined, data.series ? 3 : undefined);
    const totalScore = getTotalScores(data.period);
    const exception = isGameException(data.status);
    const closed = isGameClosed(data.status);
    const winner = getGameWinner(game);
    const finalPeriod = getFinalPeriod(game.period);
    let negativeBottom: string = "";
    
  if (data.series) {
    if (data.league === "mlb" && data.status === "InProgress") {
      negativeBottom = "-bottom-10";
    } else {
      negativeBottom = "-bottom-12";
    }
  } else {
    if (data.league === "mlb" && data.status === "InProgress") {
      negativeBottom = "-bottom-10";
    } else {
      negativeBottom = "-bottom-12 sm:-bottom-10";
    }
  }
    return (
      <>
        {/* OUTER HEADER CONTAINER */}
        <div
          className={`bg-secondary-300  h-[344px] desktop-grid:h-[369px] relative overflow-hidden`}
          >
          {data.series && (
            <div
              className="relative flex h-[26px] justify-center items-center z-[3]"
              style={{
                backgroundImage: `linear-gradient(to right, #${data.awayTeam.primaryColor} 41.63%, #${data.homeTeam.primaryColor} 57.87%)`,
              }}
            >
              <div className="text-body-sm-medium uppercase text-white">
                {data.series}
              </div>
            </div>
          )}
          {/* PARTICLES */}
          <div className="block desktop-grid:hidden">
            <img
              src={`/images/ui/game-detail-header-mobile-left.png`}
              alt=""
              className="h-[120%] z-[1] absolute left-[-240px] min-[430px]:left-[-260px] top-[-25px] opacity-40"
              style={{
                backgroundImage: `linear-gradient(to right, #${data.awayTeam.primaryColor} 240px, rgba(255, 255, 255, 0) 100%)`,
              }}
            />
          </div>
          <div className="hidden desktop-grid:block">
            <img
              src={`/images/ui/game-detail-header-right.png`}
              alt=""
              className="w-full h-[115%] z-[1] absolute desktop-grid:left-0 top-0 desktop-grid:max-w-[30%] max-w-[300px] -left-[15%] -scale-x-100 opacity-50"
              style={{
                backgroundImage: `linear-gradient(to left, #${data.awayTeam.primaryColor} 0%, rgba(255, 255, 255, 0) 70%)`,
              }}
            />
          </div>
          <div className="block desktop-grid:hidden">
            <img
              src={`/images/ui/game-detail-header-mobile-right.png`}
              alt=""
              className="h-[115%] z-[1] absolute right-[-240px] min-[430px]:right-[-260px] top-0 opacity-40"
              style={{
                backgroundImage: `linear-gradient(to left, #${data.homeTeam.primaryColor} 240px, rgba(255, 255, 255, 0) 100%)`,
              }}
            />
          </div>
          <div className="hidden desktop-grid:block">
            <img
              src={`/images/ui/game-detail-header-right.png`}
              alt=""
              className="w-full h-[115%] z-[1] absolute desktop-grid:right-0 top-0 desktop-grid:max-w-[30%] max-w-[300px] -right-[15%] opacity-50"
              style={{
                backgroundImage: `linear-gradient(to left, #${data.homeTeam.primaryColor} 0%, rgba(255, 255, 255, 0) 70%)`,
              }}
            />
          </div>
          <div className={`container h-full relative flex justify-start pt-0 items-center flex-col`}>
            {/* META */}
            <div className={`hidden desktop-grid:block absolute w-full top-[28px]`}>
              <div className="flex flex-row absolute left-0 px-6 w-full z-[1]">
                <PickChip
                showAvatars
                  totalPicks={data.totalPicks}
                  avatars={data.avatars || []}
                  totalBettors={data.totalBettors || 0}
                  totalPicksForSale={data.totalPicksForSale}
                  side="right"
                  dark
                />
              </div>
              {/* <div className="flex flex-row absolute px-6 w-full justify-between items-start gap-4 z-[1]">
                <Badge
                  color="white"
                  value={data.totalPicks}
                  showZero={true}
                  icon={
                    data.totalPicks && data.totalPicks > 0
                      ? { idKey: -1, name: "flame", gradient: "flame" }
                      : undefined
                  }
                  label={`Pick${data.totalPicks != 1 ? "s" : ""}`}
                />
                <PickGroup
                  avatars={data.avatars || []}
                  totalBettors={data.totalBettors || 0}
                  totalPicksForSale={data.totalPicksForSale}
                  side="right"
                  dark
                ></PickGroup>
              </div> */}
              {/* <div className="flex flex-row absolute h-[15.875rem] right-6 items-start">
              <div className="flex items-center gap-4">
                <span className="text-title-bold text-white">Share</span>
                <Button variant="circle" color="light">
                  <Icon name="share" size="sm"></Icon>
                </Button>
              </div>
            </div> */}
            </div>
            {/* LOGO SPLASH */}
            <div className="flex pointer-events-none z-[0] absolute w-[50%] h-full desktop-grid:w-[30rem] desktop-grid:h-[30rem] desktop-grid:-top-1/4 -left-5 opacity-20 desktop-grid:flex justify-center items-center top-0">
              <div
                className="w-full h-full absolute"
                style={{
                  background: `linear-gradient(270deg, rgba(35, 37, 40, 0) 30%, rgba(35, 37, 40, 1) 100%)`,
                }}
              ></div>
              <img
                src={data.awayTeam.logoUrl}
                className="w-full object-contain"
              />
            </div>
            <div className="flex pointer-events-none z-[0] absolute w-[50%] h-full desktop-grid:w-[30rem] desktop-grid:h-[30rem] desktop-grid:-top-1/4 -right-5 opacity-20 desktop-grid:flex justify-center items-center top-0">
              <div
                className="w-full h-full absolute"
                style={{
                  background: `linear-gradient(90deg, rgba(35, 37, 40, 0) 30%, rgba(35, 37, 40, 1) 100%)`,
                }}
              ></div>
              <img
                src={data.homeTeam.logoUrl}
                className="w-full object-contain"
              />
            </div>
            {/* INNER CONTAINER */}
            <div className="flex flex-col relative w-full max-w-[462px] mx-auto z-[1]">
              {/* HEADER */}
              <div className="flex flex-col gap-1 relative">
                {/* HEADLINE */}
                <div className="flex flex-row justify-center text-center items-center gap-2 text-headline-lg text-white mt-7 uppercase">
                  <span className="block xs:hidden">
                    {data.awayTeam.abbreviation}
                  </span>
                  <span className="hidden xs:block">{data.awayTeam.name}</span>
                  <span className="text-title-lg-medium">@</span>
                  <span className="block xs:hidden">
                    {data.homeTeam.abbreviation}
                  </span>
                  <span className="hidden xs:block">{data.homeTeam.name}</span>
                </div>
                {/* PLACEHOLDER GAP */}
                <div className="h-[14px] w-full"></div>
                {/* ANCHORED TO HEADLINE */}
                <div className="absolute top-14 left-0 right-0 w-full flex justify-center">
                  <Timestamp
                    dateTime={game.dateTime}
                    platform={game.platform}
                    className="text-title-sm-bold"
                    dark
                    sport={game.league}
                    live={live ? true : false}
                    closed={closed}
                  />
                </div>
                {/* 3 COLUMN LAYOUT - LOGOS WITH INNER CONTENT */}
                <div className={`grid grid-cols-[minmax(48px,100px)_minmax(166px,277px)_minmax(48px,100px)] justify-center relative gap-0 text-white -mt-6`}>
                  <div className="flex justify-start flex-col items-center">
                    {/* AWAY TEAM */}
                    <Link href={createTeamSlug(data.league, data.awayTeam)} className="flex flex-col items-center">
                      <TeamLogo
                        team={data.awayTeam}
                        sport={data.league}
                        size="lg"
                      />
                      <div className="text-title-sm-medium text-white whitespace-nowrap flex flex-row gap-0.5">
                        <span>{data.awayTeam.abbreviation}</span>
                        <span className="hidden sm:block">
                          {data.awayTeam.name}
                        </span>
                      </div>
                      <div className="text-body-sm text-secondary-700 mt-1">
                        {data.league === "nhl" ? (
                          <>
                            {data.awayRecord.wins}-{data.awayRecord.losses}-
                            {data.awayRecord.overtimeLosses}
                          </>
                        ) : (
                          <>
                            {data.awayRecord.wins}-{data.awayRecord.losses}
                          </>
                        )}
                      </div>
                      <span className="text-label-lg-medium text-secondary-700 uppercase mt-1">away team</span>

                    </Link>
                    
                  </div>
                  <GameDetailScore data={data} live={live} dark size="lg" />
                  <div className="flex justify-start flex-col items-center">
                    {/* HOME TEAM */}
                    <Link href={createTeamSlug(data.league, data.homeTeam)} className="flex flex-col items-center">
                      <TeamLogo
                        team={data.homeTeam}
                        sport={data.league}
                        size="lg"
                      />
                      <div className="text-title-sm-medium text-white whitespace-nowrap flex flex-row gap-0.5">
                        <span>{data.homeTeam.abbreviation}</span>
                        <span className="hidden sm:block">
                          {data.homeTeam.name}
                        </span>
                      </div>
                      <div className="text-body-sm text-secondary-700 mt-1">
                        {data.league === "nhl" ? (
                          <>
                            {data.homeRecord.wins}-{data.homeRecord.losses}-
                            {data.homeRecord.overtimeLosses}
                          </>
                        ) : (
                          <>
                            {data.homeRecord.wins}-{data.homeRecord.losses}
                          </>
                        )}
                      </div>
                      <span className="text-label-lg-medium text-secondary-700 uppercase mt-1">home team</span>
                    </Link>
                  </div>
                </div>
              </div>
              {/* LIVE MLB INNING STATUS */}
              {(live && game.league === "mlb") && (
                <div className="-mt-10 flex w-full h-[82px] items-center justify-center gap-6 scale-75 desktop-grid:scale-100">
                  <div className="relative w-[33px]">
                    <div className="inline-flex gap-[5px] flex-row flex-wrap w-[33px] rotate-45">
                      <div
                        className={`w-[14px] h-[14px] border-[2px] ${
                          live?.period.onSecond
                            ? "bg-primary-500 border-primary-500"
                            : "bg-transparent border-secondary-700"
                        }`}
                      ></div>
                      <div
                        className={`w-[14px] h-[14px] border-[2px] ${
                          live?.period.onFirst
                            ? "bg-primary-500 border-primary-500"
                            : "bg-transparent border-secondary-700"
                        }`}
                      ></div>
                      <div
                        className={`w-[14px] h-[14px] border-[2px] ${
                          live?.period.onThird
                            ? "bg-primary-500 border-primary-500"
                            : "bg-transparent border-secondary-700"
                        }`}
                      ></div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-row items-center text-title-sm-bold text-secondary-600 gap-2">
                      <span>B:</span>
                      <div className="flex flex-row gap-1 items-center">
                        <div
                          className={`w-[10px] h-[10px] rounded-full border-[2px] ${
                            live?.period.balls !== undefined &&
                            live?.period.balls >= 1
                              ? "bg-primary-500 border-primary-500"
                              : "bg-transparent border-secondary-600"
                          }`}
                        ></div>
                        <div
                          className={`w-[10px] h-[10px] rounded-full border-[2px] ${
                            live?.period.balls !== undefined &&
                            live?.period.balls >= 2
                              ? "bg-primary-500 border-primary-500"
                              : "bg-transparent border-secondary-600"
                          }`}
                        ></div>
                        <div
                          className={`w-[10px] h-[10px] rounded-full border-[2px] ${
                            live?.period.balls !== undefined &&
                            live?.period.balls >= 3
                              ? "bg-primary-500 border-primary-500"
                              : "bg-transparent border-secondary-600"
                          }`}
                        ></div>
                        <div
                          className={`w-[10px] h-[10px] rounded-full border-[2px] ${
                            live?.period.balls !== undefined &&
                            live?.period.balls >= 4
                              ? "bg-primary-500 border-primary-500"
                              : "bg-transparent border-secondary-600"
                          }`}
                        ></div>
                      </div>
                    </div>
                    <div className="flex flex-row items-center text-title-sm-bold text-secondary-600 gap-2">
                      <span>S:</span>
                      <div className="flex flex-row gap-1 items-center">
                        <div
                          className={`w-[10px] h-[10px] rounded-full border-[2px] ${
                            live?.period.strikes !== undefined &&
                            live?.period.strikes >= 1
                              ? "bg-primary-500 border-primary-500"
                              : "bg-transparent border-secondary-600"
                          }`}
                        ></div>
                        <div
                          className={`w-[10px] h-[10px] rounded-full border-[2px] ${
                            live?.period.strikes !== undefined &&
                            live?.period.strikes >= 2
                              ? "bg-primary-500 border-primary-500"
                              : "bg-transparent border-secondary-600"
                          }`}
                        ></div>
                        <div
                          className={`w-[10px] h-[10px] rounded-full border-[2px] ${
                            live?.period.strikes !== undefined &&
                            live?.period.strikes >= 3
                              ? "bg-primary-500 border-primary-500"
                              : "bg-transparent border-secondary-600"
                          }`}
                        ></div>
                      </div>
                    </div>
                    <div className="flex flex-row items-center text-title-sm-bold text-secondary-600 gap-2">
                      <span>O:</span>
                      <div className="flex flex-row gap-1 items-center">
                        <div
                          className={`w-[10px] h-[10px] rounded-full border-[2px] ${
                            live?.period.outs !== undefined &&
                            live?.period.outs >= 1
                              ? "bg-primary-500 border-primary-500"
                              : "bg-transparent border-secondary-600"
                          }`}
                        ></div>
                        <div
                          className={`w-[10px] h-[10px] rounded-full border-[2px] ${
                            live?.period.outs !== undefined &&
                            live?.period.outs >= 2
                              ? "bg-primary-500 border-primary-500"
                              : "bg-transparent border-secondary-600"
                          }`}
                        ></div>
                        <div
                          className={`w-[10px] h-[10px] rounded-full border-[2px] ${
                            live?.period.outs !== undefined &&
                            live?.period.outs >= 3
                              ? "bg-primary-500 border-primary-500"
                              : "bg-transparent border-secondary-600"
                          }`}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div
                className={`mt-4 hidden desktop-grid:flex justify-center text-label-lg-medium text-white`}
              >
                {data.temperature && <span>{data.temperature}°</span>}{" "}
                {data.stadium.name}
              </div>
            </div>
            {/* META */}
            <div className="flex desktop-grid:hidden justify-between items-center w-full gap-4 relative mb-4 z-[2]">
              <div
                className={`absolute ${negativeBottom} inset-0 flex justify-center items-center`}
              >
                {/* <PickChip
                showAvatars
                  idKey={-2}
                  totalPicks={data.totalPicks}
                  avatars={data.avatars || []}
                  totalBettors={data.totalBettors || 0}
                  totalPicksForSale={data.totalPicksForSale}
                  side="right"
                  dark
                /> */}
              </div>
              {/* <Badge
                color="white"
                value={data.totalPicks}
                showZero={true}
                icon={
                  data.totalPicks && data.totalPicks > 0
                    ? { idKey: -2, name: "flame", gradient: "flame" }
                    : undefined
                }
                label={`Pick${data.totalPicks != 1 ? "s" : ""}`}
              /> */}
              {/* Footer */}

              {/* <PickGroup
                avatars={data.avatars || []}
                totalBettors={data.totalBettors || 0}
                totalPicksForSale={data.totalPicksForSale}
                side="right"
                dark
              ></PickGroup> */}
            </div>
          </div>
        </div>
      </>
    );
  } else {
    return <div>Loading...</div>;
  }
}
